/**
 * A panel header's responsive vocabulary: how far the column has to narrow
 * before an action gives up its place in the bar for the overflow menu.
 *
 * Sits beside `SplitPanelHeader` rather than with any one panel, because every
 * panel header has the same crowding problem and answering it twice is how the
 * two answers drift apart.
 *
 * A leaf module on purpose, so it can be imported on its own — a panel's module
 * graph can reach `marker.io/sdk.ts`, which throws at import time without
 * `VITE_MARKER_PROJECT_ID`, putting anything exported from there out of a
 * test's reach.
 */

/**
 * `always` never leaves the bar; `never` is menu-only at every width. The rest
 * name the container width below which the control steps into the menu.
 */
export type BarTier = "always" | "xl" | "2xl" | "3xl" | "never";

/**
 * One row per tier, so the class the bar hides with and the width the menu
 * reasons about can never disagree.
 *
 * Both are needed because the two sides of this header answer the same question
 * by different means: the bar is inside the header's `@container` and hides with
 * a container query, while the menu portals to the document root — outside any
 * container — and so has to be told the width. `minWidth` values are the
 * Tailwind container breakpoints the classes refer to.
 *
 * Every `barClass` must be written here as a literal: Tailwind scans source
 * text, so a class name assembled at runtime is never generated and the control
 * would simply never hide.
 */
const TIER = {
  always: { barClass: "", minWidth: 0 },
  xl: { barClass: "@max-xl:hidden", minWidth: 576 },
  "2xl": { barClass: "@max-2xl:hidden", minWidth: 672 },
  "3xl": { barClass: "@max-3xl:hidden", minWidth: 768 },
  never: { barClass: "hidden", minWidth: Number.POSITIVE_INFINITY },
} satisfies Record<BarTier, { barClass: string; minWidth: number }>;

/** Hides a bar control below its tier — the `barClass` half of {@link TIER}. */
export const BAR_TIER_CLASS: Record<BarTier, string> = {
  always: TIER.always.barClass,
  xl: TIER.xl.barClass,
  "2xl": TIER["2xl"].barClass,
  "3xl": TIER["3xl"].barClass,
  never: TIER.never.barClass,
};

/**
 * Whether a control of this tier is showing in the bar at the given container
 * width — the menu's test for what it must *not* repeat, so nothing appears in
 * both places at once.
 */
export const isInBar = (tier: BarTier, containerWidth: number) =>
  containerWidth >= TIER[tier].minWidth;
